import cn from "classnames"
import { useDeferredValue, useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"

import { useGetIntegrationsList } from "@/api/useIntegrations"
import { Button, Flex, Input, Text } from "@/atoms"
import { isSelfHosted } from "@/helpers/self-hosted.helper"
import { useTranslation } from "@/hooks/useTranslation"
import { PARAMS } from "@/pages/IntegrationsPage/const"
import { createSearchParamFromTag } from "@/pages/IntegrationsPage/helpers"
import { ListIntegrationsSkeleton } from "../ListIntegrationsSkeleton"
import { DeploySensorBanner } from "./DeploySensorBanner"

import styles from "./ListIntegrationsMenu.module.scss"

export const ListIntegrationsMenu = () => {
  const { response, isLoading } = useGetIntegrationsList()
  const { data = [] } = response || {}
  const [searchParams, setSearchParams] = useSearchParams()
  const { query, all, tag: param } = PARAMS
  const search = searchParams.get(query) || ""
  const [searchValue, setSearchValue] = useState(search)
  const currentTag = (
    search ? all : searchParams.get(param) || all
  ).toLocaleLowerCase()

  const tags = useMemo(
    () =>
      (data || []).reduce(
        (acc, integration) => {
          const { tags } = integration

          tags.forEach((tag) => {
            acc[tag] = true
          })

          return acc
        },
        { [all]: true } as Record<string, boolean>
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  )

  const searchValueDeferredValue = useDeferredValue(searchValue)

  useEffect(() => {
    const hasQuery = searchParams.has(query)
    const isSearchValueDeferredValue = !!searchValueDeferredValue
    if (isSearchValueDeferredValue)
      searchParams.set(query, searchValueDeferredValue)
    else if (hasQuery) searchParams.delete(query)

    if (isSearchValueDeferredValue || hasQuery) setSearchParams(searchParams)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValueDeferredValue])

  useEffect(() => {
    if (!search && searchValue) setSearchValue("")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  const { t } = useTranslation()

  return (
    <>
      <Input
        allowClear
        disabled={isLoading}
        placeholder={t("general.search")}
        value={searchValue}
        onClear={() => {
          searchParams.delete(query)
          setSearchParams(searchParams)
        }}
        onChange={(e) => {
          setSearchValue(e.target.value)
        }}
      />
      {isLoading && <ListIntegrationsSkeleton menu />}
      {!isLoading && (
        <Flex vertical gap={8}>
          {Object.keys(tags)
            .sort((a, b) => {
              if (b === all) return 1

              return a.localeCompare(b)
            })
            .map((tag) => {
              const value = createSearchParamFromTag(tag)
              const disabled = !!search && tag !== all

              return (
                <Button
                  size="middle"
                  type="text"
                  disabled={disabled}
                  className={cn(
                    styles.button,
                    value === currentTag && styles.activeButton,
                    disabled && styles.disabledButton
                  )}
                  key={tag}
                  onClick={() => {
                    searchParams.set(param, value)
                    setSearchParams(searchParams)
                  }}
                >
                  <Text>{tag}</Text>
                </Button>
              )
            })}
        </Flex>
      )}
      {/* a request is an analytics event, which self-hosted does not send anywhere */}
      {!isLoading && !isSelfHosted() && <DeploySensorBanner />}
    </>
  )
}
